import { useState } from "react";
import { Lock } from "lucide-react";
import { changePassword } from "../../api/portal";
import { formatApiErrorDetail } from "../../utils/format";

const EMPTY_CHATBOT_USERS = [];
import PortalDataRightsSection from "./PortalDataRightsSection";
import PortalAgentTokenSection from "./PortalAgentTokenSection";

import { brandStyle } from "../../utils/branding";

export default function SettingsTab({
  clientInfo,
  api,
  chatbotUsers = [],
  onRefresh,
  clientSlug = "",
  localAgentEnabled = true,
  agentLastSyncAt = null,
  agentLastError = null,
}) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState({
    text: "",
    type: "",
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage({ text: "", type: "" });

    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        text: "Les mots de passe ne correspondent pas",
        type: "error",
      });
      return;
    }

    try {
      await changePassword(api, newPassword);
      setPasswordMessage({
        text: "Mot de passe mis à jour avec succès.",
        type: "success",
      });
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordMessage({
        text: formatApiErrorDetail(err, "Erreur lors de la mise à jour"),
        type: "error",
      });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden max-w-2xl">
        <div className="p-6 border-b border-stone-100 bg-stone-50/50">
          <h2 className="text-xl font-semibold flex items-center gap-2 text-stone-800">
            <Lock size={22} className="text-blue-500" /> Paramètres de
            sécurité
          </h2>
        </div>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-stone-800 mb-2">
            Changer le mot de passe d'accès au portail
          </h3>
          <p className="text-sm text-stone-500 mb-6 leading-relaxed">
            Ce mot de passe protège l'accès à ce portail d'administration
            client. Il ne change pas l'accès de vos utilisateurs finaux au
            chatbot.
          </p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="settings-new-password" className="text-sm font-medium text-stone-700">
                Nouveau mot de passe
              </label>
              <input
                id="settings-new-password"
                className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-sm"
                type="password"
                placeholder="Minimum 6 caractères"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="settings-confirm-password" className="text-sm font-medium text-stone-700">
                Confirmer le mot de passe
              </label>
              <input
                id="settings-confirm-password"
                className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-sm"
                type="password"
                placeholder="Répéter le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={
                !newPassword ||
                newPassword.length < 6 ||
                newPassword !== confirmPassword
              }
              className="w-full disabled:opacity-50 text-white font-medium py-2.5 px-4 rounded-xl transition-all shadow-sm hover:brightness-95 active:scale-[0.98] mt-2"
              style={brandStyle(clientInfo)}
            >
              Mettre à jour le mot de passe
            </button>
            {passwordMessage.text && (
              <div
                className={`p-3 rounded-lg text-sm font-medium text-center border mt-4 ${
                  passwordMessage.type === "error"
                    ? "bg-red-50 text-red-600 border-red-100"
                    : "bg-green-50 text-green-700 border-green-100"
                }`}
              >
                {passwordMessage.text}
              </div>
            )}
          </form>
        </div>
      </div>

      <PortalAgentTokenSection
        clientSlug={clientSlug}
        localAgentEnabled={localAgentEnabled}
        lastSyncAt={agentLastSyncAt}
        lastError={agentLastError}
      />

      <PortalDataRightsSection
        api={api}
        chatbotUsers={chatbotUsers}
        onRefresh={onRefresh}
      />
    </div>
  );
}
